<template>
  <el-card
    v-loading="loading"
    class="user-reels"
    shadow="never"
    data-testid="user-reels"
  >
    <div slot="header" class="user-reels__header">
      <span class="user-reels__title">Rolki (Reels)</span>
      <el-button
        type="text"
        icon="el-icon-refresh"
        :disabled="loading"
        @click="load"
      >
        Odśwież
      </el-button>
    </div>

    <el-alert
      v-if="loadError"
      title="Nie udało się wczytać danych o rolkach."
      type="error"
      :closable="false"
      show-icon
    />

    <template v-if="details">
      <div class="user-reels__summary">
        <div class="summary-item">
          <div class="summary-item__label">Dodatkowe rolki</div>
          <div class="summary-item__value" data-testid="reels-balance">{{ details.balance }}</div>
          <div class="summary-item__hint">dokupione i przydzielone przez admina</div>
        </div>
        <div class="summary-item">
          <div class="summary-item__label">Darmowe w tym miesiącu</div>
          <div class="summary-item__value" data-testid="reels-free-usage">{{ freeUsageText }}</div>
          <div class="summary-item__hint">
            wykorzystane; odnowienie {{ formatDate(details.usage && details.usage.reset_at) }}
          </div>
        </div>
      </div>

      <div class="user-reels__actions">
        <el-button
          type="success"
          size="small"
          icon="el-icon-plus"
          data-testid="reels-grant-open"
          @click="openCredits('grant')"
        >
          Dodaj rolki
        </el-button>
        <el-button
          type="warning"
          size="small"
          icon="el-icon-minus"
          :disabled="!details.balance"
          data-testid="reels-revoke-open"
          @click="openCredits('revoke')"
        >
          Odejmij rolki
        </el-button>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-discount"
          data-testid="reels-discount-open"
          @click="openDiscount"
        >
          Dodaj rabat
        </el-button>
      </div>

      <h4 class="user-reels__subtitle">Rabaty na pakiety rolek</h4>
      <el-table
        :data="details.discounts"
        border
        size="small"
        empty-text="Brak rabatów"
        data-testid="reels-discounts-table"
      >
        <el-table-column label="Rabat" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.percent }}%</template>
        </el-table-column>
        <el-table-column label="Użycia" width="130" align="center">
          <template slot-scope="scope">{{ formatDiscountUses(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="Ważny do" width="110" align="center">
          <template slot-scope="scope">{{ scope.row.expires_at ? formatDate(scope.row.expires_at) : 'bezterminowo' }}</template>
        </el-table-column>
        <el-table-column label="Status" width="120" align="center">
          <template slot-scope="scope">
            <el-tag :type="discountStatus(scope.row.status).type" size="mini">
              {{ discountStatus(scope.row.status).label }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Notatka" min-width="160">
          <template slot-scope="scope">{{ scope.row.note || '-' }}</template>
        </el-table-column>
        <el-table-column label="Dodał" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.created_by ? scope.row.created_by.name : '-' }}
            <div class="cell-muted">{{ formatDateTime(scope.row.created_at) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="" width="100" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.status === 'active'"
              type="danger"
              size="mini"
              :loading="revokingDiscountId === scope.row.id"
              data-testid="reels-discount-revoke"
              @click="revokeDiscount(scope.row)"
            >
              Cofnij
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <h4 class="user-reels__subtitle">Historia</h4>
      <el-tabs v-model="historyTab">
        <el-tab-pane label="Księga rolek" name="ledger">
          <el-table
            :data="details.ledger"
            border
            size="small"
            empty-text="Brak wpisów"
            data-testid="reels-ledger-table"
          >
            <el-table-column label="Data" width="150">
              <template slot-scope="scope">{{ formatDateTime(scope.row.created_at) }}</template>
            </el-table-column>
            <el-table-column label="Zmiana" width="80" align="center">
              <template slot-scope="scope">
                <span :class="scope.row.delta > 0 ? 'delta-plus' : 'delta-minus'">
                  {{ scope.row.delta > 0 ? '+' + scope.row.delta : scope.row.delta }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Saldo po" width="80" align="center" prop="balance_after" />
            <el-table-column label="Powód" min-width="150">
              <template slot-scope="scope">{{ ledgerReasonLabel(scope.row.reason) }}</template>
            </el-table-column>
            <el-table-column label="Notatka" min-width="160">
              <template slot-scope="scope">{{ scope.row.note || '-' }}</template>
            </el-table-column>
            <el-table-column label="Admin" min-width="120">
              <template slot-scope="scope">{{ scope.row.admin ? scope.row.admin.name : '-' }}</template>
            </el-table-column>
            <el-table-column label="Dotyczy" width="150">
              <template slot-scope="scope">
                {{ scope.row.reference_type ? scope.row.reference_type + ' #' + scope.row.reference_id : '-' }}
              </template>
            </el-table-column>
          </el-table>
          <div class="cell-muted table-footnote">Ostatnie 50 wpisów.</div>
        </el-tab-pane>
        <el-tab-pane label="Zakupy pakietów" name="purchases">
          <el-table
            :data="details.purchases"
            border
            size="small"
            empty-text="Brak zakupów"
            data-testid="reels-purchases-table"
          >
            <el-table-column label="Data" width="150">
              <template slot-scope="scope">{{ formatDateTime(scope.row.created_at) }}</template>
            </el-table-column>
            <el-table-column label="Pakiet" min-width="100" prop="pack" />
            <el-table-column label="Rolki" width="70" align="center" prop="credits" />
            <el-table-column label="Cena bazowa" width="110" align="right">
              <template slot-scope="scope">{{ formatPriceGr(scope.row.base_price_gr) }}</template>
            </el-table-column>
            <el-table-column label="Rabat" min-width="140">
              <template slot-scope="scope">
                <span v-if="scope.row.discount_percent">
                  {{ scope.row.discount_percent }}%
                  <span class="cell-muted">({{ discountSourceLabel(scope.row.discount_source) }})</span>
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="Zapłacono" width="110" align="right">
              <template slot-scope="scope">{{ formatPriceGr(scope.row.price_gr) }}</template>
            </el-table-column>
            <el-table-column label="Platforma" width="90" prop="platform" />
            <el-table-column label="Status" width="150" align="center">
              <template slot-scope="scope">
                <el-tag :type="purchaseStatus(scope.row.status).type" size="mini">
                  {{ purchaseStatus(scope.row.status).label }}
                </el-tag>
                <div v-if="scope.row.paid_at" class="cell-muted">{{ formatDateTime(scope.row.paid_at) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="Transakcja" width="100" align="center" prop="transaction_id" />
          </el-table>
          <div class="cell-muted table-footnote">Ostatnie 20 zakupów.</div>
        </el-tab-pane>
      </el-tabs>
    </template>

    <el-dialog
      :visible.sync="creditsDialogVisible"
      :title="creditsMode === 'grant' ? 'Dodaj rolki' : 'Odejmij rolki'"
      width="460px"
      append-to-body
    >
      <el-form label-width="110px" size="small" @submit.native.prevent>
        <el-form-item label="Liczba rolek">
          <el-input-number
            v-model="creditsForm.amount"
            :min="1"
            :max="creditsMax"
            data-testid="reels-credits-amount"
          />
          <span v-if="creditsMode === 'revoke'" class="field-hint">saldo: {{ details ? details.balance : 0 }}</span>
        </el-form-item>
        <el-form-item label="Notatka">
          <el-input
            v-model="creditsForm.note"
            :maxlength="noteMaxLength"
            placeholder="opcjonalnie, np. promocja dla grupy"
            show-word-limit
            data-testid="reels-credits-note"
          />
        </el-form-item>
        <el-form-item v-if="creditsMode === 'grant'" label="">
          <el-checkbox v-model="creditsForm.notify" data-testid="reels-credits-notify">Powiadom użytkownika</el-checkbox>
          <div class="cell-muted">{{ notifyHint }}</div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="creditsDialogVisible = false">Anuluj</el-button>
        <el-button
          :type="creditsMode === 'grant' ? 'success' : 'warning'"
          size="small"
          :loading="saving"
          data-testid="reels-credits-submit"
          @click="submitCredits"
        >
          {{ creditsMode === 'grant' ? 'Dodaj' : 'Odejmij' }}
        </el-button>
      </div>
    </el-dialog>

    <el-dialog
      :visible.sync="discountDialogVisible"
      title="Dodaj rabat na pakiety rolek"
      width="560px"
      append-to-body
    >
      <el-form label-width="110px" size="small" @submit.native.prevent>
        <discount-fields :form="discountForm" />
        <el-form-item label="Notatka">
          <el-input
            v-model="discountForm.note"
            :maxlength="noteMaxLength"
            placeholder="opcjonalnie"
            show-word-limit
            data-testid="reels-discount-note"
          />
        </el-form-item>
        <el-form-item label="">
          <el-checkbox v-model="discountForm.notify" data-testid="reels-discount-notify">Powiadom użytkownika</el-checkbox>
          <div class="cell-muted">{{ notifyHint }}</div>
        </el-form-item>
      </el-form>
      <div class="cell-muted">
        Rabaty się nie sumują - przy zakupie wygrywa najwyższy. Rabat zużywa się dopiero przy opłaconej płatności.
      </div>
      <div slot="footer">
        <el-button size="small" @click="discountDialogVisible = false">Anuluj</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="saving"
          data-testid="reels-discount-submit"
          @click="submitDiscount"
        >
          Dodaj rabat
        </el-button>
      </div>
    </el-dialog>
  </el-card>
</template>

<script>
import {
  getUserReels,
  grantUserReels,
  revokeUserReels,
  createUserReelDiscount,
  revokeUserReelDiscount,
} from '@/api/reels';
import {
  MAX_AMOUNT,
  MAX_DISCOUNT_PERCENT,
  NOTE_MAX_LENGTH,
  DISCOUNT_SOURCE_LABELS,
  ledgerReasonLabel,
  discountStatus,
  purchaseStatus,
  formatPriceGr,
  formatDate,
  formatDateTime,
  formatDiscountUses,
  emptyDiscountForm,
  discountPayload,
  noteOrNull,
  reelsCountLabel,
  NOTIFY_HINT,
  notifySuffix,
} from '@/utils/reels';
import DiscountFields from '@/views/reels/components/DiscountFields';

/**
 * Sekcja "Rolki (Reels)" w edycji użytkownika: saldo dodatkowych rolek, darmowe rolki
 * w miesiącu, przydział / odjęcie rolek, rabaty na pakiety, księga i zakupy.
 * Każda mutacja zwraca pełny stan (jak GET), więc odświeżamy z odpowiedzi.
 */
export default {
  name: 'UserReels',
  components: { DiscountFields },
  props: {
    userId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadError: false,
      saving: false,
      details: null,
      historyTab: 'ledger',
      creditsDialogVisible: false,
      creditsMode: 'grant',
      creditsForm: { amount: 1, note: '', notify: true },
      discountDialogVisible: false,
      discountForm: Object.assign(emptyDiscountForm(), { note: '', notify: true }),
      revokingDiscountId: null,
      noteMaxLength: NOTE_MAX_LENGTH,
      notifyHint: NOTIFY_HINT,
    };
  },
  computed: {
    freeUsageText() {
      const reel = this.details && this.details.usage ? this.details.usage.reel : null;
      if (!reel) {
        return '-';
      }
      return reel.used + ' z ' + (reel.limit === null || reel.limit === undefined ? 'bez limitu' : reel.limit);
    },
    creditsMax() {
      if (this.creditsMode === 'revoke' && this.details) {
        return Math.max(1, Math.min(MAX_AMOUNT, this.details.balance));
      }
      return MAX_AMOUNT;
    },
  },
  watch: {
    userId() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    ledgerReasonLabel,
    discountStatus,
    purchaseStatus,
    formatPriceGr,
    formatDate,
    formatDateTime,
    formatDiscountUses,
    discountSourceLabel(source) {
      return DISCOUNT_SOURCE_LABELS[source] || source || '-';
    },
    async load() {
      this.loading = true;
      this.loadError = false;
      try {
        this.details = await getUserReels(this.userId);
      } catch (error) {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    openCredits(mode) {
      this.creditsMode = mode;
      this.creditsForm = { amount: 1, note: '', notify: true };
      this.creditsDialogVisible = true;
    },
    async submitCredits() {
      const amount = this.creditsForm.amount;
      if (!Number.isInteger(amount) || amount < 1 || amount > MAX_AMOUNT) {
        this.$message.warning('Podaj liczbę rolek od 1 do ' + MAX_AMOUNT + '.');
        return;
      }
      const grant = this.creditsMode === 'grant';
      if (!grant && amount > this.details.balance) {
        this.$message.warning('Użytkownik ma tylko ' + this.details.balance + ' dodatkowych rolek.');
        return;
      }

      try {
        await this.$confirm(
          (grant ? 'Dodać ' : 'Odjąć ') + reelsCountLabel(amount) + (grant ? ' użytkownikowi?' : ' z salda użytkownika?'),
          'Potwierdzenie',
          {
            confirmButtonText: grant ? 'Dodaj' : 'Odejmij',
            cancelButtonText: 'Anuluj',
            type: 'warning',
          }
        );
      } catch (cancel) {
        return;
      }

      const payload = { amount, note: noteOrNull(this.creditsForm.note) };
      if (grant) {
        // Korekty (odjęcie) nigdy nie powiadamiają - backend nie przyjmuje tam `notify`.
        payload.notify = this.creditsForm.notify;
      }
      this.saving = true;
      try {
        this.details = grant
          ? await grantUserReels(this.userId, payload)
          : await revokeUserReels(this.userId, payload);
        this.creditsDialogVisible = false;
        this.$message.success(grant ? 'Dodano rolki. ' + notifySuffix(payload.notify) : 'Odjęto rolki.');
      } catch (error) {
        // Komunikat błędu pokazuje już interceptor axios (src/utils/request.js).
        if (error && error.response && error.response.data && error.response.data.code === 'insufficient_balance') {
          this.load();
        }
      } finally {
        this.saving = false;
      }
    },
    openDiscount() {
      this.discountForm = Object.assign(emptyDiscountForm(), { note: '', notify: true });
      this.discountDialogVisible = true;
    },
    async submitDiscount() {
      const form = this.discountForm;
      if (!Number.isInteger(form.percent) || form.percent < 1 || form.percent > MAX_DISCOUNT_PERCENT) {
        this.$message.warning('Rabat musi wynosić od 1 do ' + MAX_DISCOUNT_PERCENT + '%.');
        return;
      }
      if (!form.unlimited && !Number.isInteger(form.maxUses)) {
        this.$message.warning('Podaj liczbę użyć albo zaznacz "bez limitu".');
        return;
      }

      const payload = Object.assign(discountPayload(form), { note: noteOrNull(form.note), notify: form.notify });
      this.saving = true;
      try {
        this.details = await createUserReelDiscount(this.userId, payload);
        this.discountDialogVisible = false;
        this.$message.success('Dodano rabat ' + payload.percent + '%. ' + notifySuffix(payload.notify));
      } catch (error) {
        // Komunikat błędu pokazuje już interceptor axios.
      } finally {
        this.saving = false;
      }
    },
    async revokeDiscount(discount) {
      try {
        await this.$confirm(
          'Cofnąć rabat ' + discount.percent + '%? Użytkownik nie będzie mógł go już wykorzystać.',
          'Potwierdzenie',
          {
            confirmButtonText: 'Cofnij rabat',
            cancelButtonText: 'Anuluj',
            type: 'warning',
          }
        );
      } catch (cancel) {
        return;
      }

      this.revokingDiscountId = discount.id;
      try {
        this.details = await revokeUserReelDiscount(this.userId, discount.id);
        this.$message.success('Rabat cofnięty.');
      } catch (error) {
        // Komunikat błędu pokazuje już interceptor axios; 404 = rabat zniknął, odświeżamy.
        this.load();
      } finally {
        this.revokingDiscountId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.user-reels {
  margin: 30px 0;
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__title {
    font-size: 16px;
    font-weight: 600;
  }
  &__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;
  }
  &__actions {
    margin-bottom: 20px;
  }
  &__subtitle {
    margin: 20px 0 10px;
    font-size: 14px;
  }
}
.summary-item {
  min-width: 220px;
  padding: 12px 16px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
  &__label {
    font-size: 13px;
    color: #606266;
  }
  &__value {
    margin: 4px 0;
    font-size: 24px;
    font-weight: 600;
    color: #303133;
  }
  &__hint {
    font-size: 12px;
    color: #909399;
  }
}
.cell-muted {
  font-size: 12px;
  color: #909399;
}
.table-footnote {
  margin-top: 6px;
}
.field-hint {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
.delta-plus {
  color: #67c23a;
  font-weight: 600;
}
.delta-minus {
  color: #f56c6c;
  font-weight: 600;
}
</style>
